import React, { useMemo, useRef, useEffect } from "react"
import { Suspense } from "react"
import { Canvas, useLoader, useThree } from "react-three-fiber"
import {
  OrbitControls,
  AdaptiveEvents,
  AdaptiveDpr,
  Environment,
  Reflector,
  Loader,
} from "@react-three/drei"
import { isMobile } from "react-device-detect"
import { SVGLoader } from "three/examples/jsm/loaders/SVGLoader"
import * as THREE from "three"
import FabricWrapper from "../components/FabricWrapper"
import FabricWrapperMobile from "../components/FabricWrapperMobile"
import { useStore } from "../store"
import Model from "../components/Dice"
import gsap from "gsap"
import { Vector3 } from "three"

const Face = ({ face, position, rotation, svg }) => {
  const { paths } = useLoader(
    SVGLoader,
    `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(svg)))}`
  )

  return (
    <group
      name={face}
      rotation={[rotation.x, rotation.y, rotation.z]}
      position={[position.x, position.y, position.z]}
      scale={[0.15, 0.15, 0.15]}
    >
      {paths.map(path => {
        const shapes = path.toShapes()
        return shapes.map((shape, i) => {
          const geometry = new THREE.ExtrudeGeometry(shape, {
            depth: 10,
            steps: 2,
            bevelEnabled: false,
          })

          return (
            <mesh key={i} args={[geometry]}>
              {!isMobile ? (
                <meshPhysicalMaterial
                  roughness={0.3}
                  clearcoat={1}
                  clearcoatRoughness={0.2}
                  color={"black"}
                />
              ) : (
                <meshStandardMaterial roughness={0.3} color={"black"} />
              )}
            </mesh>
          )
        })
      })}
    </group>
  )
}

const Die = () => {
  const ref = useRef()
  const { setMesh, face, faces } = useStore(state => state)

  const { camera } = useThree()

  useEffect(() => {
    setMesh(ref)
  }, [ref])

  useEffect(() => {
    if (face.length > 0) {
      gsap.to(camera.position, {
        x: face[0],
        y: face[1],
        z: face[2],
      })
    }
  }, [face])

  return (
    <group ref={ref} position={[0, isMobile ? 10 : 0, 0]}>
      {faces.map(e => (
        <Face key={e.face} {...e} />
      ))}
      <Model position={[0, 0, 0]} />
    </group>
  )
}

const IndexPage = () => {
  const {
    setCameraFace,
    exportSTL,
    setFaces,
    faces,
    setCurrentFace,
    currentFace,
    mesh,
  } = useStore(state => state)

  useEffect(() => {
    const faces = []
    const facesNum = 6
    // rotation={[-Math.PI / 2, Math.PI, Math.PI]}
    //  position={[-9.6, 7.75, -9.9]}
    const depth = 1.0
    const position = [
      new Vector3(-9.6, 7.5 + depth * 0.35, -9.9),
      new Vector3(-7.5 - depth * 0.35, 10, -9.9 + depth * 0.5),
      new Vector3(9.9 - depth * 0.5, 10, 7.5 - depth * 1.135),
      new Vector3(9.9 - depth * 0.5, 10, -7.5 - depth * 0.35),
      new Vector3(7.5 - depth * 1.135, 10, -9.9 + depth * 0.5),
      new Vector3(-9.6, -7.5 - depth * 0.35, 9.9),
    ]
    const rotation = [
      new Vector3(-Math.PI / 2, Math.PI, Math.PI),
      new Vector3(0, Math.PI * 0.5, Math.PI),
      new Vector3(0, 0, Math.PI * 3),
      new Vector3(0, 0, Math.PI * 3),
      new Vector3(0, Math.PI * 0.5, Math.PI),
      new Vector3(Math.PI / 2, Math.PI, Math.PI),
    ]

    const focus = [
      new Vector3(0, 100, 0),
      new Vector3(-100, 0, 0),
      new Vector3(0, 0, 100),
      new Vector3(0, 0, -100),
      new Vector3(100, 0, 0),
      new Vector3(0, -100, 0),
    ]

    for (let i = 0; i < facesNum; i++) {
      faces.push({
        face: i + 1,
        svg: "",
        active: false,
        dieType: `D${facesNum}`,
        dieTheme: `default`,
        position: position[i],
        rotation: rotation[i],
        focus: focus[i],
        iconName: "empty face",
        fabric: [],
      })
    }
    setFaces(faces)
  }, [])

  return (
    <div
      style={{ backgroundColor: "#E2F0CB" }}
      className="h-screen font-title relative overflow-hidden select-none bg-gray-100"
    >
      <Canvas
        concurrent
        shadowMap
        pixelRatio={[1, 2]}
        camera={{ position: [-60, 50, 30], fov: 40 }}
      >
        <OrbitControls minDistance={isMobile ? 90 : 20} maxDistance={225} />
        {!isMobile && <fog attach="fog" args={["lightpink", 0, 1000]} />}
        <AdaptiveDpr pixelated />
        <AdaptiveEvents />
        <Suspense fallback={null}>
          <Environment background={false} preset={"apartment"} />
          <Die />
          {isMobile ? (
            <mesh rotation={[-Math.PI * 0.5, 0, 0]} position={[0, 2, 0]}>
              <planeGeometry args={[40, 40]} />
              <meshBasicMaterial color="#666" />
            </mesh>
          ) : (
            <Reflector
              resolution={1024}
              receiveShadow
              mirror={0}
              blur={[500, 100]}
              mixBlur={1}
              mixStrength={0.5}
              depthScale={1}
              minDepthThreshold={0.8}
              maxDepthThreshold={1}
              position={[0, -8, 0]}
              rotation={[-Math.PI / 2, 0, Math.PI]}
              args={[70, 70]}
            >
              {(Material, props) => (
                <Material
                  metalness={0.5}
                  color="#C7CEEA"
                  roughness={0.5}
                  {...props}
                />
              )}
            </Reflector>
          )}
        </Suspense>
      </Canvas>
      <Loader />

      <div className="absolute top-2 right-2 text-center hover:text-black">
        <span className="uppercase font-bold text-xl align-middle">
          Die face:
        </span>
        {faces.map(d => {
          return (
            <button
              key={d.face}
              className={` text-white p-1 h-8 w-8 rounded-full font-bold  ml-1 hover:bg-gray-600 outline-none  focus:outline-none ${
                d.face === currentFace ? `bg-red-600` : `bg-gray-900`
              }`}
              onClick={e => {
                setCurrentFace(d.face)
                setCameraFace([d.focus.x, d.focus.y, d.focus.z])
              }}
            >
              {d.face}
            </button>
          )
        })}

        <button
          className={`bg-green-300 font-bold  py-2 px-4 rounded inline-flex items-center ml-2 hover:bg-gray-900 hover:text-white align-middle ${
            isMobile ? `mt-2` : `mt-0`
          }`}
          onClick={() => exportSTL(mesh)}
        >
          <svg
            className="w-4 h-4 mr-2 hover:text-white"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
          >
            <path d="M13 8V2H7v6H2l8 8 8-8h-5zM0 18h20v2H0v-2z" />
          </svg>
          <span>Export to STL</span>
        </button>
      </div>

      {!isMobile && (
        <div className="absolute bottom-4 right-4 ">
          <div className="mt-5">
            {faces.map(face => {
              return (
                <p
                  key={face.face}
                  className="text-right uppercase font-bold text-xs font-mono mt-1 mx-2"
                >
                  face {face.face} icon:{" "}
                  <span className="text-gray-600">
                    {face.iconName.replace(".svg", "")}
                  </span>
                </p>
              )
            })}
          </div>
        </div>
      )}

      {!isMobile ? (
        <div className="absolute top-0 left-0 text-center hover:text-black w-72">
          <FabricWrapper face={1} />
          <p className="absolute bottom-10 left-4 rounded-md  p-2">
            @alvarmars
          </p>
          <a
            href="https://game-icons.net/"
            target="_blank"
            className="absolute bottom-2 left-4 rounded-md  hover:bg-gray-600 hover:text-white p-2"
          >
            Icons from <span className="text-pink-700">♥</span> game-icons.net
          </a>
        </div>
      ) : (
        <div className="fixed inset-x-1 top-0 left-2 text-center hover:text-black w-full h-full">
          <FabricWrapperMobile face={1} />
        </div>
      )}
    </div>
  )
}

export default IndexPage
